import React, { useState, useCallback } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import useAuth from '../../hooks/use-auth';
import { v4 as uuidv4 } from 'uuid';

import FloatingButton from './FloatingButton';
import FloatingChat from './FloatingChat';
import { websoketURL } from '../../hydra';
import { EVENTS, trackEvent } from '../../analytics';
import { AIWorkflow } from '../../contexts/ai-workflow';
import * as CrashReporter from '../../crash-reporter';

import { useUpdateEffect } from 'react-use';
import { publishAnswerStreamEvent } from '../../answer-stream/events';
import { isAnswerStreamEvent } from '../../answer-stream/types';
export const STATES = {
  CONNECTING: 'CONNECTING',
  CLOSED: 'CLOSED',
  OPEN: 'OPEN',
  ERROR: 'ERROR',
};
const CLIENT_ID = 'ENKI';

export default function AIChat({
  contentId,
  missionId,
  activityId,
  projectId,
  discussionContext,
  // CMS preview only. The content being discussed is an unsaved draft with no
  // row to look up, so it initializes the isolated preview chat after connect.
  previewContext = /** @type {any} */ (null),
  shouldShowChatIcon = true,
  className = '',
  answerStreamSubjectType = /** @type {import('../../answer-stream/types').AnswerStreamSubjectType | null} */ (
    null
  ),
  answerStreamSubjectId = /** @type {number | null} */ (null),
}) {
  const {
    messageContext,
    pendingUserMessage,
    setPendingUserMessage,
    pendingAiMessage,
    setPendingAiMessage,
    clearTempMessageContext,
    isChatOpen,
    setIsChatOpen,
    chat,
    setChat,
  } = React.useContext(AIWorkflow);

  const [didReceiveInitialMessages, setDidReceiveInitialMessages] =
    useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [isPreviewContextReady, setIsPreviewContextReady] = useState(
    !previewContext
  );

  const { user, getTokenSilently } = useAuth();

  const [inputMessage, setInputMessage] = useState('');
  const [isReceivingMessage, setIsReceivingMessage] = useState(false);
  const hasAnswerStreamSubject =
    answerStreamSubjectType &&
    Number.isInteger(answerStreamSubjectId) &&
    answerStreamSubjectId > 0;

  const addChatBreadcrumb = useCallback(
    (message, metadata = {}) => {
      CrashReporter.addBreadcrumb({
        message: `AI Chat - ${message}`,
        metadata: {
          contentId,
          missionId,
          activityId,
          projectId,
          discussionContext,
          ...metadata,
        },
        type: CrashReporter.BREADCRUMB_TYPE.USER,
      });
    },
    [contentId, missionId, activityId, projectId, discussionContext]
  );

  const handleChatError = useCallback(
    (error, context = '') => {
      const errorMessage = error?.message || 'Unknown error';
      const errorContext = context ? ` - ${context}` : '';

      CrashReporter.reportError(error);
      addChatBreadcrumb(`Error${errorContext}`, {
        error: errorMessage,
        stack: error?.stack,
      });

      setConnectionError(errorMessage);
      setIsReceivingMessage(false);
    },
    [addChatBreadcrumb]
  );

  const onInputChange = (value) => {
    setInputMessage(value);
    if (!value) {
      clearTempMessageContext();
    }
  };

  const categorizeWebSocketError = useCallback((event) => {
    const code = event?.code;
    const reason = event?.reason || '';

    let category = 'Unknown';
    let description = 'Unknown error';

    if (code === 1000) {
      category = 'Normal';
      description = 'Normal closure';
    } else if (code === 1001) {
      category = 'Going Away';
      description = 'Endpoint going away';
    } else if (code === 1002) {
      category = 'Protocol Error';
      description = 'Protocol error';
    } else if (code === 1003) {
      category = 'Unsupported Data';
      description = 'Unsupported data received';
    } else if (code === 1006) {
      category = 'Abnormal Closure';
      description =
        'Connection closed abnormally (network issue, server down, etc.)';
    } else if (code === 1007) {
      category = 'Invalid Data';
      description = 'Invalid frame payload data';
    } else if (code === 1008) {
      category = 'Policy Violation';
      description = 'Policy violation';
    } else if (code === 1009) {
      category = 'Message Too Large';
      description = 'Message too large';
    } else if (code === 1011) {
      category = 'Server Error';
      description = 'Server error';
    } else if (code === 1012) {
      category = 'Service Restart';
      description = 'Service restart';
    } else if (code === 1013) {
      category = 'Try Again Later';
      description = 'Try again later';
    } else if (code === 1014) {
      category = 'Bad Gateway';
      description = 'Bad gateway';
    } else if (code === 1015) {
      category = 'TLS Handshake';
      description = 'TLS handshake failure';
    } else if (code >= 4000 && code <= 4999) {
      category = 'Application Error';
      description = `Application-specific error: ${reason}`;
    }

    return { category, description, code, reason };
  }, []);

  const getSocketUrl = useCallback(async () => {
    try {
      addChatBreadcrumb('Getting socket URL');
      const token = await getTokenSilently();
      const socketUrl = `${websoketURL}/?token=${token}&clientId=${CLIENT_ID}`;

      // Add detailed debugging information
      addChatBreadcrumb('Socket URL generated successfully', {
        baseUrl: websoketURL,
        clientId: CLIENT_ID,
        environment: process.env.REACT_APP_ENV,
        tokenLength: token?.length,
        hasToken: !!token,
      });

      // Log to console for easier debugging in development
      if (process.env.REACT_APP_ENV === 'development') {
        console.log('AI Chat WebSocket Connection Details:', {
          socketUrl: socketUrl.replace(/token=[^&]+/, 'token=***'),
          baseUrl: websoketURL,
          clientId: CLIENT_ID,
          environment: process.env.REACT_APP_ENV,
          hasToken: !!token,
        });
      }

      return socketUrl;
    } catch (error) {
      console.error('Failed to generate socket URL:', error);
      handleChatError(error, 'Failed to get socket URL');
      throw error;
    }
  }, [getTokenSilently, addChatBreadcrumb, handleChatError]);

  const validateMessageData = useCallback((data) => {
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid message data: not an object');
    }
    if (!data.type || typeof data.type !== 'string') {
      throw new Error('Invalid message data: missing or invalid type');
    }
    return true;
  }, []);

  const handleWebSocketMessage = useCallback(
    (messageEvent) => {
      try {
        addChatBreadcrumb('Received WebSocket message');

        if (!messageEvent?.data) {
          throw new Error('No message data received');
        }

        const data = JSON.parse(messageEvent.data);
        validateMessageData(data);

        addChatBreadcrumb('Processing message', { messageType: data.type });

        if (isAnswerStreamEvent(data)) {
          publishAnswerStreamEvent(data);
        } else if (data.type === 'get-messages') {
          if (!data.chatId) {
            throw new Error('No chatId in get-messages response');
          }

          setChat({
            ...chat,
            id: data.chatId,
            messages: Array.isArray(data.messages) ? [...data.messages] : [],
            dailyMessagesCount: data.dailyMessagesCount || 0,
          });
          setDidReceiveInitialMessages(true);
          setConnectionError(null);

          addChatBreadcrumb('Initial messages loaded', {
            messageCount: data.messages?.length || 0,
            dailyCount: data.dailyMessagesCount || 0,
          });
        } else if (data.type === 'get-message-chunk') {
          if (!data.chatId) {
            throw new Error('No chatId in get-message-chunk response');
          }

          if (!chat.messages || chat.messages.length === 0) {
            throw new Error('No messages in chat to update');
          }

          const lastMessage = chat.messages[chat.messages.length - 1];
          if (!lastMessage) {
            throw new Error('No last message found to update');
          }

          setChat({
            ...chat,
            id: data.chatId,
            messages: [
              ...chat.messages.slice(0, chat.messages.length - 1),
              {
                ...lastMessage,
                content: data.message?.content || '',
                is_error: data.message?.is_error || false,
              },
            ],
          });
        } else if (data.type === 'get-message-chunk-end') {
          setChat({
            ...chat,
            dailyMessagesCount: (chat.dailyMessagesCount || 0) + 1,
          });
          setIsReceivingMessage(false);

          addChatBreadcrumb('Message completed');
        } else if (data.type === 'preview-context-ready') {
          setIsPreviewContextReady(true);
          setConnectionError(null);
          addChatBreadcrumb('Preview context initialized');
        } else if (data.type === 'preview-context-error') {
          throw new Error(
            data.message || 'AI chat could not load this preview'
          );
        } else {
          console.warn('Unknown message type', data);
          addChatBreadcrumb('Unknown message type', { messageType: data.type });
        }
      } catch (error) {
        handleChatError(error, 'Failed to process WebSocket message');
      }
    },
    [chat, setChat, addChatBreadcrumb, handleChatError, validateMessageData]
  );

  const { sendJsonMessage, readyState } = useWebSocket(getSocketUrl, {
    share: true,
    queryParams: {
      ...(contentId && { contentId }),
      ...(missionId && { missionId }),
      ...(activityId && { activityId }),
      ...(projectId && { projectId }),
      ...(hasAnswerStreamSubject && {
        answerStreamSubjectType,
        answerStreamSubjectId,
      }),
      discussionContext,
    },
    onMessage: handleWebSocketMessage,
    onOpen: () => {
      addChatBreadcrumb('WebSocket connection opened');
      console.log('AI Chat WebSocket opened successfully');
      setIsReceivingMessage(false);
      setConnectionError(null);
      setIsPreviewContextReady(!previewContext);
      setDidReceiveInitialMessages(false);
    },
    onClose: (event) => {
      const errorInfo = categorizeWebSocketError(event);
      const closeDetails = {
        code: event?.code,
        reason: event?.reason || 'No reason provided',
        wasClean: event?.wasClean,
        errorCategory: errorInfo.category,
        errorDescription: errorInfo.description,
        timestamp: new Date().toISOString(),
      };

      addChatBreadcrumb('WebSocket connection closed', closeDetails);
      console.log('AI Chat WebSocket closed:', closeDetails);

      // Log whether we will attempt to reconnect
      const willReconnect =
        closeDetails.code === 1006 ||
        closeDetails.code === 1011 ||
        closeDetails.code === 1012 ||
        closeDetails.code === 1013;
      console.log(`Will attempt reconnection: ${willReconnect}`);

      setIsReceivingMessage(false);
      setIsPreviewContextReady(!previewContext);
    },
    onError: (event) => {
      const errorDetails = {
        message: event?.message || 'WebSocket connection error',
        type: event?.type || 'error',
        target: event?.target?.url || 'unknown',
        timestamp: new Date().toISOString(),
      };

      console.error('AI Chat WebSocket error:', errorDetails);
      addChatBreadcrumb('WebSocket error', errorDetails);
    },
    shouldReconnect: (closeEvent) => {
      // Don't reconnect on normal closure or client-initiated closure
      const code = closeEvent?.code;

      // Never reconnect on these codes
      if (code === 1000 || code === 1001 || code === 1005) {
        return false;
      }

      // Reconnect on common error codes that indicate temporary issues
      return (
        code === 1006 ||
        code === 1011 ||
        code === 1012 ||
        code === 1013 ||
        code === 1014 ||
        code === 1015 ||
        !code
      ); // Also reconnect if no code
    },
    reconnectAttempts: 3,
    reconnectInterval: 3000,
  });

  React.useEffect(() => {
    if (
      !previewContext ||
      !didReceiveInitialMessages ||
      readyState !== ReadyState.OPEN ||
      isPreviewContextReady
    ) {
      return;
    }

    const contextBytes = new TextEncoder().encode(
      JSON.stringify(previewContext)
    ).length;
    const maxContextBytes = 96 * 1024;

    if (contextBytes > maxContextBytes) {
      handleChatError(
        new Error('This preview is too large for AI chat'),
        'Preview context too large'
      );
      return;
    }

    sendJsonMessage({
      action: 'set-preview-context',
      previewContext,
    });
    addChatBreadcrumb('Initializing preview context', {
      contextBytes,
      questionCount: previewContext.questions?.length ?? 0,
    });
  }, [
    previewContext,
    didReceiveInitialMessages,
    readyState,
    isPreviewContextReady,
    sendJsonMessage,
    addChatBreadcrumb,
    handleChatError,
  ]);

  React.useEffect(() => {
    if (
      !previewContext ||
      !didReceiveInitialMessages ||
      isPreviewContextReady ||
      connectionError
    ) {
      return;
    }

    const timeout = window.setTimeout(() => {
      handleChatError(
        new Error('AI chat timed out while loading this preview'),
        'Preview context initialization timeout'
      );
    }, 10000);

    return () => window.clearTimeout(timeout);
  }, [
    previewContext,
    didReceiveInitialMessages,
    isPreviewContextReady,
    connectionError,
    handleChatError,
  ]);

  const sendMessage = useCallback(
    async (input) => {
      if (!input?.trim()) {
        addChatBreadcrumb('Attempted to send empty message');
        return;
      }

      if (!chat?.id) {
        const error = new Error('No chat ID available for sending message');
        handleChatError(error, 'No chat ID');
        return;
      }

      if (!user?.id) {
        const error = new Error('No user ID available for sending message');
        handleChatError(error, 'No user ID');
        return;
      }

      if (previewContext && !isPreviewContextReady) {
        handleChatError(
          new Error('AI chat is still loading the preview'),
          'Preview context not ready'
        );
        return;
      }

      try {
        addChatBreadcrumb('Sending message', { messageLength: input.length });

        setInputMessage('');

        const messageToSend = {
          chat_id: chat.id,
          content: input,
          sender_user_id: user.id,
          is_bot: false,
          is_error: false,
          vote: 0,
          feedback: null,
          id: chat.messages.length + 1, // temp id for avoiding duplicate keys
          message_context: messageContext,
        };

        const messageToPopulate = {
          chat_id: chat.id,
          content: '',
          sender_user_id: null,
          is_bot: true,
          is_error: false,
          vote: 0,
          feedback: null,
          id: chat.messages.length + 2, // temp id for avoiding duplicate keys
        };

        setChat({
          ...chat,
          messages: [...chat.messages, messageToSend, messageToPopulate],
        });

        trackEvent({
          event: EVENTS.AI.MESSAGE.SEND,
          properties: {
            contentId,
            missionId,
            activityId,
            discussionContext,
            messageContextType: messageContext?.context_type ?? null,
            messageContextContent: messageContext?.context_content ?? null,
          },
        });

        setIsReceivingMessage(true);

        sendJsonMessage({
          action: 'send-message',
          chatId: chat.id,
          message: input,
          messageContext: messageContext,
          clientId: CLIENT_ID,
        });

        clearTempMessageContext();
        addChatBreadcrumb('Message sent successfully');
      } catch (error) {
        handleChatError(error, 'Failed to send message');
      }
    },
    [
      contentId,
      missionId,
      activityId,
      discussionContext,
      sendJsonMessage,
      setChat,
      chat,
      user,
      messageContext,
      clearTempMessageContext,
      addChatBreadcrumb,
      handleChatError,
      previewContext,
      isPreviewContextReady,
    ]
  );

  useUpdateEffect(() => {
    if (
      pendingUserMessage &&
      !isReceivingMessage &&
      (!previewContext || isPreviewContextReady)
    ) {
      setInputMessage(pendingUserMessage);
      sendMessage(pendingUserMessage);
      setPendingUserMessage();
    }
  }, [
    pendingUserMessage,
    isReceivingMessage,
    previewContext,
    isPreviewContextReady,
  ]);

  React.useEffect(() => {
    if (pendingAiMessage) {
      try {
        setChat({
          ...chat,
          messages: [
            ...chat.messages,
            {
              chat_id: chat.id,
              content: pendingAiMessage,
              sender_user_id: null,
              is_bot: true,
              is_error: false,
              vote: 0,
              feedback: null,
              id: uuidv4(),
              message_context: messageContext,
            },
          ],
        });
        setPendingAiMessage();
        addChatBreadcrumb('Pending AI message added');
      } catch (error) {
        handleChatError(error, 'Failed to add pending AI message');
      }
    }
  }, [
    pendingAiMessage,
    chat,
    setPendingAiMessage,
    setChat,
    messageContext,
    addChatBreadcrumb,
    handleChatError,
  ]);

  if (shouldShowChatIcon && !isChatOpen) {
    return <FloatingButton onOpen={() => setIsChatOpen(true)} />;
  }

  if (!isChatOpen) {
    return null;
  }

  function getConnectionState() {
    if (connectionError) {
      return STATES.ERROR;
    }
    if (readyState === ReadyState.OPEN && didReceiveInitialMessages) {
      return STATES.OPEN;
    }
    if (readyState === ReadyState.CLOSED) {
      return STATES.CLOSED;
    }
    return STATES.CONNECTING;
  }

  const handleRetryConnection = () => {
    addChatBreadcrumb('User initiated connection retry');
    setConnectionError(null);
    setDidReceiveInitialMessages(false);
    // The useWebSocket hook will automatically reconnect
  };

  return (
    <FloatingChat
      className={className}
      onClose={() => {
        setIsChatOpen(false);
        clearTempMessageContext();
        addChatBreadcrumb('Chat closed by user');
      }}
      inputMessage={inputMessage}
      setInputMessage={onInputChange}
      connectionState={getConnectionState()}
      chat={chat}
      sendMessage={sendMessage}
      isReceivingMessage={isReceivingMessage}
      shouldLock={false}
      dailyMessagesCount={chat.dailyMessagesCount}
      connectionError={connectionError}
      onRetryConnection={handleRetryConnection}
    />
  );
}
